import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { LIST_MEDIA } from '~/services/training';
import { ClipboardIcon } from '@heroicons/react/24/outline';

interface MediaItem {
  _id: string;
  name: string;
  url: string;
  mimetype: string;
  size: number;
  description: string;
}

interface DaisyUIMediaLibraryProps {
  agentId: string;
  onSelectMedia: (media: MediaItem) => void;
  isOpen: boolean;
  onClose: () => void;
}

export const DaisyUIMediaLibrary = ({
  agentId,
  onSelectMedia,
  isOpen,
  onClose,
}: DaisyUIMediaLibraryProps) => {
  const [t] = useTranslation('media_library');
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [fetchMedia, { loading }] = useLazyQuery(LIST_MEDIA, {
    onCompleted: (data) => {
      const response = data.response;

      if (response && !response.hasError) {
        // Map the API response to our MediaItem interface
        const mappedItems = response.data.results.map((item: any) => ({
          _id: item._id,
          name: item.name,
          url: item.url,
          mimetype:
            item.mimetype ||
            (item.type === 'IMAGE' ? 'image/jpeg' : 'video/mp4'),
          size: item.size || 0,
          description: item.description || item.alt_description || '',
        }));

        if (nextCursor) {
          setMediaItems((prev) => [...prev, ...mappedItems]);
        } else {
          setMediaItems(mappedItems);
        }
        setNextCursor(response.data.next_cursor);
        setHasMoreItems(
          response.data.results.length > 0 && response.data.next_cursor,
        );
      }
    },
  });

  useEffect(() => {
    if (isOpen && agentId) {
      setMediaItems([]);
      setNextCursor(null);
      setHasMoreItems(true);
      fetchMedia({
        variables: {
          data: {
            agent_id: agentId,
            next_cursor: null,
            limit: 12,
          },
        },
      });
    }
  }, [isOpen, agentId, fetchMedia]);

  const loadMore = () => {
    if (nextCursor && hasMoreItems && !loading) {
      fetchMedia({
        variables: {
          data: {
            agent_id: agentId,
            next_cursor: nextCursor,
            limit: 12,
          },
        },
      });
    }
  };

  const handleCopyUrl = async (item: MediaItem) => {
    try {
      const url = `${import.meta.env.VITE_API_URL}/media/${item.url}`;
      await navigator.clipboard.writeText(url);
      setCopiedId(item._id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box flex h-5/6 w-11/12 max-w-5xl flex-col">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-base-content text-2xl font-bold">
              📂 {t('media_library', 'Media Library')}
            </h2>
            <p className="text-base-content/60 text-sm">
              {t(
                'select_media_description',
                'Select media to insert into your content',
              )}
            </p>
          </div>
          <button onClick={onClose} className="btn btn-sm btn-circle btn-ghost">
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {loading && mediaItems.length === 0 ? (
            <div className="flex h-40 items-center justify-center">
              <span className="loading loading-spinner loading-lg"></span>
            </div>
          ) : mediaItems.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
              {mediaItems.map((item) => (
                <div
                  key={item._id}
                  className="card card-compact bg-base-100 border-base-200 hover:border-primary cursor-pointer border shadow-sm transition-shadow hover:shadow-md"
                  onClick={() => {
                    onSelectMedia(item);
                    onClose();
                  }}
                >
                  <figure className="bg-base-200 aspect-square">
                    {item.mimetype.startsWith('image') ? (
                      <img
                        src={`${import.meta.env.VITE_API_URL}/media/${
                          item.url
                        }`}
                        alt={item.name}
                        className="h-full w-full object-cover"
                      />
                    ) : item.mimetype.startsWith('video') ? (
                      <video
                        src={`${import.meta.env.VITE_API_URL}/media/${
                          item.url
                        }`}
                        className="h-full w-full object-cover"
                        controls
                        preload="metadata"
                        playsInline
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <div className="bg-base-200 flex h-full w-full items-center justify-center">
                        <div className="text-center">
                          <div className="mb-2 text-4xl">📄</div>
                          <div className="text-base-content/60 text-xs">
                            Document
                          </div>
                        </div>
                      </div>
                    )}
                  </figure>
                  <div className="card-body p-3">
                    <h3
                      className="card-title truncate text-xs"
                      title={item.name}
                    >
                      {item.name}
                    </h3>
                    <p
                      className="text-base-content/60 truncate text-xs"
                      title={item.description}
                    >
                      {item.description ||
                        t('no_description', 'No description')}
                    </p>
                    <div className="card-actions mt-1">
                      <button
                        className="btn btn-xs btn-outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyUrl(item);
                        }}
                        title={t('copy_url', 'Copy URL')}
                      >
                        <ClipboardIcon className="h-4 w-4" />
                        {copiedId === item._id
                          ? t('copied', 'Copied')
                          : t('copy_url', 'Copy URL')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-base-content/60 flex h-40 flex-col items-center justify-center">
              <div className="mb-4 text-6xl">📁</div>
              <p className="text-lg font-medium">
                {t('no_media_found', 'No media found')}
              </p>
              <p className="text-sm">
                {t('upload_media_hint', 'Upload some media to get started')}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-base-300 mt-6 flex items-center justify-between border-t pt-4">
          <div className="text-base-content/60 text-sm">
            {mediaItems.length > 0 && (
              <span>
                {mediaItems.length} {t('items_loaded', 'items loaded')}
                {hasMoreItems &&
                  ' • ' + t('scroll_for_more', 'Scroll for more')}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {hasMoreItems && (
              <button
                onClick={loadMore}
                disabled={loading}
                className="btn btn-sm btn-outline"
              >
                {loading ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    {t('loading', 'Loading...')}
                  </>
                ) : (
                  t('load_more', 'Load More')
                )}
              </button>
            )}
            <button onClick={onClose} className="btn btn-sm">
              {t('close', 'Close')}
            </button>
          </div>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button onClick={onClose}>close</button>
      </form>
    </div>
  );
};
